import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card, InputAdornment
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';

const Examination = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ className: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [examinations, setExaminations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolId, setSchoolId] = useState(null);

  // Fetch schoolId from the logged-in user's document
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        setSchoolId(userDoc.data().schoolId);
      } else {
        toast.error("User data not found");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      toast.error("Error fetching user data");
    }
  };

  // Fetch examinations for this school
  const fetchExaminations = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'examinations'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExaminations(data);
    } catch (err) {
      console.error("Error fetching examinations:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchSchoolId();
    };
    init();
  }, []);

  useEffect(() => {
    if (schoolId) fetchExaminations(schoolId);
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ className: '', description: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { className, description } = formData;

    if (!className.trim() || !description.trim()) {
      toast.error("All fields are required");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    try {
      const examData = { className, description, schoolId };

      if (editMode && editId) {
        await updateDoc(doc(db, 'examinations', editId), examData);
        toast.success("Examination updated");
      } else {
        await addDoc(collection(db, 'examinations'), examData);
        toast.success("Examination added");
      }

      fetchExaminations(schoolId);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (exam) => {
    setFormData({ className: exam.className || '', description: exam.description || '' });
    setEditId(exam.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'examinations', id));
      toast.success("Examination deleted");
      fetchExaminations(schoolId);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting examination");
    }
  };

  const filteredExams = examinations.filter(exam =>
    exam.className.toLowerCase().includes(searchTerm)
  );

  const paginated = filteredExams.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Examination</Button>
        <Typography variant="h5" fontWeight="bold">Examinations</Typography>
      </Stack>

      <Card>
        <Box sx={{ p: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Examination Name</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">No examinations found</TableCell>
                </TableRow>
              ) : (
                paginated.map((exam, index) => (
                  <TableRow key={exam.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(exam)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(exam.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{exam.className}</TableCell>
                    <TableCell>{exam.description}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={filteredExams.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Examination</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Examination Name"
                name="className"
                value={formData.className}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Examination;